import { fabric } from "fabric"
import { flagEnabled } from "../flags"
import { sizeUnit } from "@/common/locales"
import { isMobile } from "@/common/util"
import Calc from "@/common/util/calculator"
import BaseFixmethod, { type FixmethodPosition } from "@/signomatic/app/fixmethods/base"
import CircleObject from "@/signomatic/app/objects/circle"
import GroupObject from "@/signomatic/app/objects/group"

export const DEFAULT_COUNT = 2
export const DEFAULT_DISTANCE_MM = 4
export const DEFAULT_DISTANCE_SCREW_MM = 5
export const DEFAULT_SIZE_MM = 4
export const COUNT_MIN = 1
export const COUNT_MAX = 4
export const DIST_MIN_MM = 4
export const DIST_MAX_MM = 20
export const DIST_STEP_MM = 1
export const SIZE_MIN_MM = 3
export const SIZE_MAX_MM = 5
export const SIZE_STEP_MM = 0.5
const CUSTOM_HOLE_RANGE = [1, 2, 4]
const HOLE_CLEARANCE_MM = 2 // Minimum distance between hole edge and hole edge or sign edge

export const CORNER_RADIUS_MIN_MM = 0 // This will fallback to 0.5mm in printCutlineRounding
export const CORNER_RADIUS_DEFAULT_MAX_MM = 30
const NON_CORNER_PATTERNS = [
  "top_center",
  "bottom_center",
  "middle_left",
  "middle_right",
  "center_x",
  "center_y",
  "rotated_rect",
]

export const defaultPatternByHoleCount: Record<number, string> = {
  1: "top_left",
  2: "center_x",
  4: "rect",
}

type Axis = "x" | "y" | "both"

type HoleStrategyData = {
  positions: FixmethodPosition[]
  widthPx: number
  heightPx: number
}

export function patternValidationAxis(pattern: string): Axis {
  switch (pattern) {
    case "middle_left":
    case "middle_right":
    case "center_x":
      return "x"
    case "top_center":
    case "bottom_center":
    case "center_y":
      return "y"
    default:
      return "both"
  }
}

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n))
const roundToStep = (n: number, step: number, direction?: string) => {
  switch (direction) {
    case "up":
      return Math.ceil(n / step) * step
    case "down":
    default:
      return Math.floor(n / step) * step
  }
}

function getScrewSpec(material: SignState["material"], cappedScrew: boolean) {
  switch (material) {
    case "aluminium_lacquered":
      return { screwHoleDiameterMm: 4, headDiameterMm: cappedScrew ? 10 : 7 }
    case "regular":
    case "aluminium":
    case "stainless":
      return { screwHoleDiameterMm: 3, headDiameterMm: 5 }
    default:
      return { screwHoleDiameterMm: 3, headDiameterMm: 5 }
  }
}

export function cornerRadiusFixmethodConditions(state: SignState) {
  const cornerRadiusFixmethodConditions =
    ["none", "tape", "self_adhesive_pad", "custom_holes"].includes(state.fixmethod || "") ||
    (state.fixmethod === "screw" && state.customHoleCount) ||
    (state.fixmethod === "tape_and_screw" && state.customHoleCount)

  return cornerRadiusFixmethodConditions
}

function distanceTo45deg(holeDistance: number, cornerRadius: number) {
  const sqrt2 = Math.SQRT2
  const invSqrt2 = 1 / sqrt2
  const factor = 1 - invSqrt2
  return sqrt2 * (holeDistance - cornerRadius * factor)
}

// returns a max corner radius that scales with aspect ratio for balanced rounding
export function maxCornerRadiusBySize(width: number, height: number) {
  const ratio = Math.max(width, height) / Math.min(width, height)
  const factor = 3 - Math.min(1, (ratio - 1) / 2) * 0.5

  return Math.floor(Math.min(width, height) / factor - 3)
}

function maxCornerRadius(
  holeDistance: number,
  holeDiameter: number,
  clearance: number,
  fixmethod: string,
  width: number,
  height: number,
  pattern: string
) {
  // Not limited by holes or their placement
  if (["none", "tape", "self_adhesive_pad"].includes(fixmethod) || NON_CORNER_PATTERNS.includes(pattern)) {
    return maxCornerRadiusBySize(width, height)
  }

  const sqrt2 = Math.SQRT2
  const invSqrt2 = 1 / sqrt2
  const factor = 1 - invSqrt2

  const minDistance = holeDiameter / 2 + clearance
  const cornerRadiusMax = (holeDistance - minDistance / sqrt2) / factor

  return Math.floor(cornerRadiusMax)
}

// Returns the shortest sign side along which the hole pattern is moving. For 2 holes the distance is halved,
// as holes move towards each other.
function getRelevantSide(width: number, height: number, pattern: string, holeCount?: number) {
  const shortestSide = Math.min(width, height)
  const axis = patternValidationAxis(pattern)

  if (axis === "y") {
    return holeCount === 2 ? height / 2 : height
  }

  if (axis === "x") {
    return holeCount === 2 ? width / 2 : width
  }

  if (holeCount === 2) {
    const widthIsShortest = width === shortestSide
    const heightIsShortest = height === shortestSide

    const isTopOrBottom = ["top", "bottom"].includes(pattern)
    const isLeftOrRight = ["left", "right"].includes(pattern)
    const isCenter = ["center_x", "center_y"].includes(pattern)

    if (
      (widthIsShortest && isTopOrBottom) ||
      (heightIsShortest && isLeftOrRight) ||
      (widthIsShortest && heightIsShortest) ||
      isCenter
    ) {
      return shortestSide / 2
    }
  }

  return shortestSide
}

export function validateHoleSize(state: SignState, requested: number) {
  const {
    customHoleCount: customHoleCountState,
    customHoleDistanceMm: customHoleDistanceMmState,
    cornerRadiusMm,
    customHoleControlPattern,
    width,
    height,
  } = state

  const customHoleCount = customHoleCountState || DEFAULT_COUNT
  const customHoleDistanceMm = customHoleDistanceMmState || DEFAULT_DISTANCE_MM
  let holeSizeMm = roundToStep(clamp(requested, SIZE_MIN_MM, SIZE_MAX_MM), SIZE_STEP_MM)

  const relevantSide = getRelevantSide(
    width,
    height,
    state.customHoleControlPattern || defaultPatternByHoleCount[customHoleCount],
    customHoleCount
  )
  const diameter = holeSizeMm
  const radius = diameter / 2
  const clearance = HOLE_CLEARANCE_MM // min clearance between hole-edge and hole-hole
  const distance = customHoleDistanceMm

  // Check how many holes fit
  const fitsOneHole =
    distance - radius >= clearance &&
    relevantSide - distance - radius >= (customHoleCount > 1 ? clearance / 2 : clearance)
  const fitsTwoHoles = fitsOneHole && relevantSide - distance - radius >= clearance / 2
  const fitsFourHoles =
    width / 2 - distance - radius >= clearance / 2 && height / 2 - distance - radius >= clearance / 2

  let limitExceeded = false
  let maxSizeMm = SIZE_MAX_MM

  // Include radius
  let holeToRoundedCorner = distanceTo45deg(distance, cornerRadiusMm || 0)
  // Exclude holeToRoundedCorner for non corner patterns
  if (NON_CORNER_PATTERNS.includes(customHoleControlPattern || "")) {
    holeToRoundedCorner = 100
  }

  // Calculate max hole size
  if (customHoleCount === 1) {
    limitExceeded = !fitsOneHole
    maxSizeMm = 2 * Math.min(distance - clearance, relevantSide - distance - clearance, holeToRoundedCorner - clearance)
  } else if (customHoleCount === 2) {
    limitExceeded = !fitsTwoHoles
    maxSizeMm =
      2 * Math.min(relevantSide - distance - clearance / 2, distance - clearance, holeToRoundedCorner - clearance)
  } else if (customHoleCount === 4) {
    limitExceeded = !fitsFourHoles
    maxSizeMm =
      2 *
      Math.min(
        width / 2 - distance - clearance / 2,
        height / 2 - distance - clearance / 2,
        distance - clearance,
        holeToRoundedCorner - clearance
      )
  }

  maxSizeMm = Math.max(SIZE_MIN_MM, maxSizeMm)
  holeSizeMm = roundToStep(clamp(holeSizeMm, SIZE_MIN_MM, maxSizeMm), SIZE_STEP_MM)

  return {
    value: holeSizeMm,
    limitExceeded,
    max: Math.min(maxSizeMm, SIZE_MAX_MM),
  }
}

export function validateHoleDistance(state: SignState, requested: number) {
  const {
    width,
    height,
    fixmethod,
    customHoleCount: customHoleCountState,
    customHoleSizeMm: customHoleSizeMmState,
    customHoleControlPattern,
    customHoleDistanceMm,
    material,
    cornerRadiusMm,
  } = state

  const customHoleCount = customHoleCountState || DEFAULT_COUNT
  const customHoleSizeMm = customHoleSizeMmState || DEFAULT_SIZE_MM
  const customHolesFixmethod = fixmethod === "custom_holes"
  const cappedScrew = fixmethod === "screw_capped"
  const distMinFixmethodMm = fixmethod === "custom_holes" ? DIST_MIN_MM : DEFAULT_DISTANCE_SCREW_MM

  const { headDiameterMm } = getScrewSpec(material, cappedScrew)

  const relevantSide = getRelevantSide(
    width,
    height,
    customHoleControlPattern || defaultPatternByHoleCount[customHoleCount],
    customHoleCount
  )
  const diameterMm = customHolesFixmethod ? customHoleSizeMm : headDiameterMm
  const diameter = diameterMm
  const radius = diameter / 2
  const clearance = HOLE_CLEARANCE_MM // min clearance between hole-edge and hole-hole

  // Min hole distance calculation for corner radius presence
  const defaultDistance = fixmethod === "custom_holes" ? DEFAULT_DISTANCE_MM : DEFAULT_DISTANCE_SCREW_MM
  const cornerToHole = distanceTo45deg(requested || defaultDistance, 0)
  const roundedCornerToHole = distanceTo45deg(requested || defaultDistance, cornerRadiusMm || 0)
  const cornerToRoundedCorner = cornerToHole - roundedCornerToHole
  const edgeToRoundedCorner = cornerToRoundedCorner / Math.SQRT2
  const roundedCornerToClearance = clearance / Math.SQRT2
  let minEdgeDistanceWithRadius = Math.round(edgeToRoundedCorner + roundedCornerToClearance + radius)
  // Exclude holeToRoundedCorner for non corner patterns
  if (NON_CORNER_PATTERNS.includes(customHoleControlPattern || "")) {
    minEdgeDistanceWithRadius = 0
  }

  const minEdgeDistance = radius + clearance
  const minBetweenHoles = diameter + clearance

  let max = 0

  // Calculate max distance
  if (customHoleCount === 1) {
    max = Math.max(relevantSide - minEdgeDistance, distMinFixmethodMm)
  } else if (customHoleCount === 2) {
    max = relevantSide - (radius + clearance / 2)
  } else if (customHoleCount === 4) {
    max = Math.min((width - minBetweenHoles) / 2, (height - minBetweenHoles) / 2)
  }

  const minRaw = Math.min(Math.max(minEdgeDistance, minEdgeDistanceWithRadius, DIST_MIN_MM), customHoleDistanceMm || 0)
  const maxRaw = Math.min(max, DIST_MAX_MM)
  const min = roundToStep(minRaw, DIST_STEP_MM)
  max = roundToStep(maxRaw, DIST_STEP_MM)

  let validMm = requested
  validMm = roundToStep(clamp(validMm, min, max), DIST_STEP_MM)

  const limitExceeded = requested < min || requested > max

  return { value: validMm, limitExceeded, min, max }
}

export function validateHoleCount(state: SignState, requested: number, pattern?: string) {
  const {
    width,
    height,
    fixmethod,
    material,
    customHoleCountMm,
    customHoleSizeMm: customHoleSizeMmState,
    customHoleDistanceMm: customHoleDistanceMmState,
    customHoleControlPattern,
    cornerRadiusMm,
  } = state

  const customHoleSizeMm = customHoleSizeMmState || DEFAULT_SIZE_MM
  const customHoleDistanceMm = customHoleDistanceMmState || DEFAULT_DISTANCE_MM
  const customHolesFixmethod = fixmethod === "custom_holes"
  const cappedScrew = fixmethod === "screw_capped"
  const { headDiameterMm } = getScrewSpec(material, cappedScrew)

  const relevantSide = (holeCount?: number) => {
    const count = holeCount || requested
    const side = getRelevantSide(
      width,
      height,
      pattern || customHoleControlPattern || defaultPatternByHoleCount[requested],
      count
    )

    return side
  }
  const diameter = customHolesFixmethod ? customHoleSizeMm : headDiameterMm
  const radius = diameter / 2
  const clearance = HOLE_CLEARANCE_MM // min clearance between hole-edge and hole-hole
  const distance = customHoleDistanceMm

  // Check how many holes fit
  const fitsOneHole = relevantSide(1) - distance - radius >= clearance
  const fitsTwoHoles = relevantSide(2) - distance - radius >= clearance / 2
  const fitsFourHoles =
    width / 2 - distance - radius >= clearance / 2 && height / 2 - distance - radius >= clearance / 2

  const defaultDistance = fixmethod === "custom_holes" ? DEFAULT_DISTANCE_MM : DEFAULT_DISTANCE_SCREW_MM
  const maxAllowedRadius = maxCornerRadius(
    customHoleDistanceMm || defaultDistance,
    customHoleSizeMm || DEFAULT_SIZE_MM,
    HOLE_CLEARANCE_MM,
    fixmethod || "",
    width,
    height,
    pattern || customHoleControlPattern || ""
  )

  let value = requested
  let limitExceeded = false
  let max = 4

  if (!CUSTOM_HOLE_RANGE.includes(requested)) {
    value = customHoleCountMm
  }

  if (requested === 4 && !fitsFourHoles) {
    value = fitsTwoHoles ? 2 : 1
    limitExceeded = true
  } else if (requested === 2 && !fitsTwoHoles) {
    value = 1
    limitExceeded = true
  } else if (requested === 1 && !fitsOneHole) {
    limitExceeded = true
  }

  if (fitsFourHoles) {
    max = 4
  } else if (fitsTwoHoles) {
    max = 2
  } else {
    max = 1
  }

  if ((cornerRadiusMm || 0) > maxAllowedRadius) {
    limitExceeded = true
  }

  return { value, limitExceeded, max }
}

export function validateCornerRadius(state: SignState, requested: number) {
  const {
    width,
    height,
    material,
    cornerRadiusMm,
    customHoleDistanceMm,
    customHoleSizeMm,
    customHoleControlPattern,
    fixmethod,
  } = state
  const cappedScrew = fixmethod === "screw_capped"
  const { headDiameterMm } = getScrewSpec(material, cappedScrew)
  const diameter = fixmethod === "custom_holes" ? customHoleSizeMm : headDiameterMm
  const defaultDistance = fixmethod === "custom_holes" ? DEFAULT_DISTANCE_MM : DEFAULT_DISTANCE_SCREW_MM

  const max = maxCornerRadius(
    customHoleDistanceMm || defaultDistance,
    diameter || DEFAULT_SIZE_MM,
    HOLE_CLEARANCE_MM,
    fixmethod || "",
    width,
    height,
    customHoleControlPattern || ""
  )

  let value = cornerRadiusMm
  let limitExceeded = false

  if (requested > max) {
    limitExceeded = true
    value = max
  } else {
    value = requested
  }

  return { value, limitExceeded, max }
}

export default class CustomHolesFixmethod extends BaseFixmethod {
  positions(): FixmethodPosition[] {
    const {
      widthPx,
      heightPx,
      material,
      fixmethod,
      customHoleCount,
      customHoleDistanceMm,
      customHoleSizeMm,
      customHoleControlPattern,
    } = this.state

    const customHolesFixmethod = fixmethod === "custom_holes"
    const cappedScrew = fixmethod === "screw_capped"
    const { headDiameterMm: headDiameter, screwHoleDiameterMm: screwHoleDiameter } = getScrewSpec(material, cappedScrew)

    const holeCount = customHoleCount || DEFAULT_COUNT
    const controlPattern = customHoleControlPattern || defaultPatternByHoleCount[holeCount]
    let distance = Calc.mmToPx(customHoleDistanceMm || DEFAULT_DISTANCE_MM)
    let distanceAdjusted = Calc.mmToPx(
      (customHoleDistanceMm || DEFAULT_DISTANCE_MM) - (material === "stainless" ? 0.38 : 0.75)
    )
    const diameter = Calc.mmToPx(customHolesFixmethod ? customHoleSizeMm || DEFAULT_SIZE_MM : screwHoleDiameter)
    const headDiameterPx = Calc.mmToPx(headDiameter)

    const positions: FixmethodPosition[] = []
    const defaults = { diameter, headDiameter: headDiameterPx }

    if (holeCount === 1) {
      distance = customHolesFixmethod ? distance : distanceAdjusted

      if (controlPattern === "top_left") {
        positions.push({ ...defaults, left: distance, top: distance })
        positions.push({ ...defaults, left: widthPx - distance, top: heightPx - distance, stretch: true })
      } else if (controlPattern === "top_right") {
        positions.push({ ...defaults, left: widthPx - distance, top: distance })
        positions.push({ ...defaults, left: distance, top: heightPx - distance, stretch: true })
      } else if (controlPattern === "top_center") {
        positions.push({ ...defaults, left: widthPx / 2, top: distance })
        positions.push({ ...defaults, left: widthPx / 2, top: heightPx - distance, stretch: true })
      } else if (controlPattern === "bottom_left") {
        positions.push({ ...defaults, left: distance, top: heightPx - distance })
        positions.push({ ...defaults, left: widthPx - distance, top: distance, stretch: true })
      } else if (controlPattern === "bottom_right") {
        positions.push({ ...defaults, left: widthPx - distance, top: heightPx - distance })
        positions.push({ ...defaults, left: distance, top: distance, stretch: true })
      } else if (controlPattern === "bottom_center") {
        positions.push({ ...defaults, left: widthPx / 2, top: heightPx - distance })
        positions.push({ ...defaults, left: widthPx / 2, top: distance, stretch: true })
      } else if (controlPattern === "middle_left") {
        positions.push({ ...defaults, left: distance, top: heightPx / 2 })
        positions.push({ ...defaults, left: widthPx - distance, top: heightPx / 2, stretch: true })
      } else if (controlPattern === "middle_right") {
        positions.push({ ...defaults, left: widthPx - distance, top: heightPx / 2 })
        positions.push({ ...defaults, left: distance, top: heightPx / 2, stretch: true })
      }
    } else if (holeCount === 2) {
      distanceAdjusted = customHolesFixmethod ? distance : distanceAdjusted

      if (controlPattern === "center_x") {
        positions.push({ ...defaults, left: distance, top: heightPx / 2 })
        positions.push({ ...defaults, left: widthPx - distance, top: heightPx / 2 })
      } else if (controlPattern === "top") {
        positions.push({ ...defaults, left: distance, top: distanceAdjusted })
        positions.push({ ...defaults, left: widthPx - distance, top: distanceAdjusted })
        positions.push({ ...defaults, left: distance, top: heightPx - distanceAdjusted, stretch: true })
        positions.push({ ...defaults, left: widthPx - distance, top: heightPx - distanceAdjusted, stretch: true })
      } else if (controlPattern === "bottom") {
        positions.push({ ...defaults, left: distance, top: heightPx - distanceAdjusted })
        positions.push({ ...defaults, left: widthPx - distance, top: heightPx - distanceAdjusted })
        positions.push({ ...defaults, left: distance, top: distanceAdjusted, stretch: true })
        positions.push({ ...defaults, left: widthPx - distance, top: distanceAdjusted, stretch: true })
      } else if (controlPattern === "left") {
        positions.push({ ...defaults, left: distanceAdjusted, top: distance })
        positions.push({ ...defaults, left: distanceAdjusted, top: heightPx - distance })
        positions.push({ ...defaults, left: widthPx - distanceAdjusted, top: distance, stretch: true })
        positions.push({ ...defaults, left: widthPx - distanceAdjusted, top: heightPx - distance, stretch: true })
      } else if (controlPattern === "right") {
        positions.push({ ...defaults, left: widthPx - distanceAdjusted, top: distance })
        positions.push({ ...defaults, left: widthPx - distanceAdjusted, top: heightPx - distance })
        positions.push({ ...defaults, left: distanceAdjusted, top: distance, stretch: true })
        positions.push({ ...defaults, left: distanceAdjusted, top: heightPx - distance, stretch: true })
      } else if (controlPattern === "center_y") {
        positions.push({ ...defaults, left: widthPx / 2, top: distance })
        positions.push({ ...defaults, left: widthPx / 2, top: heightPx - distance })
      }
    } else if (holeCount === 4) {
      if (controlPattern === "rotated_rect") {
        positions.push({ ...defaults, left: widthPx / 2, top: distance })
        positions.push({ ...defaults, left: distance, top: heightPx / 2 })
        positions.push({ ...defaults, left: widthPx / 2, top: heightPx - distance })
        positions.push({ ...defaults, left: widthPx - distance, top: heightPx / 2 })
      } else if (controlPattern === "rect") {
        positions.push({ ...defaults, left: distance, top: distance })
        positions.push({ ...defaults, left: distance, top: heightPx - distance })
        positions.push({ ...defaults, left: widthPx - distance, top: distance })
        positions.push({ ...defaults, left: widthPx - distance, top: heightPx - distance })
      }
    }

    return positions
  }

  addCustomHoleDimensions() {
    const { customHoleCount, customHoleControlPattern, widthPx, heightPx } = this.state
    const positions = this.positions().filter(p => !p.stretch)
    const holeCount = customHoleCount || DEFAULT_COUNT
    const controlPattern = customHoleControlPattern || defaultPatternByHoleCount[holeCount]
    const canvas = this.drawables.signCanvas

    const addW = (x1: number, x2: number, label: number) => canvas.add(this.widthDimensionsGroup(x1, x2, label))
    const addH = (y1: number, y2: number, label: number) => canvas.add(this.heightDimensionsGroup(y1, y2, label))

    const ctx = { positions, widthPx, heightPx }

    const singleHoleStrategies: Record<string, (c: HoleStrategyData) => void> = {
      top_left: ({ positions }) => {
        const p = positions[0]

        addW(0, p.left, p.left)
        addH(0, p.top, p.top)
      },
      top_right: ({ positions, widthPx }) => {
        const p = positions[0]

        addW(p.left, widthPx, widthPx - p.left)
        addH(0, p.top, p.top)
      },
      top_center: ({ positions }) => {
        const p = positions[0]

        addH(0, p.top, p.top)
      },
      bottom_left: ({ positions, heightPx }) => {
        const p = positions[0]

        addW(0, p.left, p.left)
        addH(p.top, heightPx, heightPx - p.top)
      },
      bottom_right: ({ positions, widthPx, heightPx }) => {
        const p = positions[0]

        addW(p.left, widthPx, widthPx - p.left)
        addH(p.top, heightPx, heightPx - p.top)
      },
      bottom_center: ({ positions, heightPx }) => {
        const p = positions[0]

        addH(p.top, heightPx, heightPx - p.top)
      },
      middle_left: ({ positions }) => {
        const p = positions[0]

        addW(0, p.left, p.left)
      },
      middle_right: ({ positions, widthPx }) => {
        const p = positions[0]

        addW(p.left, widthPx, widthPx - p.left)
      },
    }

    const doubleHoleStrategies: Record<string, (c: HoleStrategyData) => void> = {
      top: ({ positions }) => {
        const [p1, p2] = positions

        addW(0, p1.left, p1.left)
        addW(p1.left, p2.left, Math.abs(p2.left - p1.left))
      },
      bottom: ({ positions }) => {
        const [p1, p2] = positions

        addW(0, p1.left, p1.left)
        addW(p1.left, p2.left, Math.abs(p2.left - p1.left))
      },
      left: ({ positions }) => {
        const [p1, p2] = positions

        addH(0, p1.top, p1.top)
        addH(p1.top, p2.top, Math.abs(p2.top - p1.top))
      },
      right: ({ positions }) => {
        const [p1, p2] = positions

        addH(0, p1.top, p1.top)
        addH(p1.top, p2.top, Math.abs(p2.top - p1.top))
      },
      center_x: ({ positions }) => {
        const [p1, p2] = positions

        addW(0, p1.left, p1.left)
        addW(p1.left, p2.left, Math.abs(p2.left - p1.left))
      },
      center_y: ({ positions }) => {
        const [p1, p2] = positions

        addH(0, p1.top, p1.top)
        addH(p1.top, p2.top, Math.abs(p2.top - p1.top))
      },
    }

    const quadHoleStrategies: Record<string, (c: HoleStrategyData) => void> = {
      rect: ({ positions }) => {
        const sortedByY = [...positions].sort((a, b) => a.top - b.top)
        const [tl, tr] = sortedByY.slice(0, 2).sort((a, b) => a.left - b.left)
        const [bl] = sortedByY.slice(2, 4).sort((a, b) => a.left - b.left)

        addW(0, tl.left, tl.left)
        addW(tl.left, tr.left, Math.abs(tr.left - tl.left))
        addH(0, tl.top, tl.top)
        addH(tl.top, bl.top, Math.abs(bl.top - tl.top))
      },
      rotated_rect: ({ positions }) => {
        const top = positions.reduce((a, b) => (a.top < b.top ? a : b))
        const bottom = positions.reduce((a, b) => (a.top > b.top ? a : b))
        const left = positions.reduce((a, b) => (a.left < b.left ? a : b))
        const right = positions.reduce((a, b) => (a.left > b.left ? a : b))

        addW(0, left.left, left.left)
        addW(left.left, right.left, Math.abs(right.left - left.left))
        addH(0, top.top, top.top)
        addH(top.top, bottom.top, Math.abs(bottom.top - top.top))
      },
    }

    const strategiesByCount: Record<number, Record<string, (c: HoleStrategyData) => void>> = {
      1: singleHoleStrategies,
      2: doubleHoleStrategies,
      4: quadHoleStrategies,
    }

    const strategySet = strategiesByCount[holeCount]
    const strategy = strategySet && strategySet[controlPattern]

    return strategy && strategy(ctx)
  }

  private buildDrawable(holeStroke: string, holeFill: string, outerWidth: number) {
    const { widthPx, heightPx } = this.state
    const items: fabric.Object[] = []
    const positions = this.positions()
    const customHoleTabOpened = this.drawables.isCustomHoleTabOpened
    const holesOverlayFlag = flagEnabled("holes-overlay-9389")

    if (positions.length === 1 && positions[0].left && positions[0].top && positions[0].diameter) {
      const holeRadius = positions[0].diameter / 2
      const left = positions[0].left
      const top = positions[0].top

      items.push(
        new CircleObject({
          radius: holeRadius,
          left,
          top,
          stroke: holeStroke,
          strokeWidth: outerWidth,
          startAngle: 0,
          endAngle: Calc.toDeg(Math.PI * 2),
          fill: holeFill,
        }).addClass("hang", "custom_holes")
      )

      // stretch out
      items.push(
        new CircleObject({
          radius: holeRadius,
          left: widthPx - left,
          top: heightPx - top,
          startAngle: 0,
          endAngle: Calc.toDeg(Math.PI * 2),
          stroke: "transparent",
          strokeWidth: outerWidth,
          fill: "transparent",
        })
      )
    } else {
      for (const e of positions) {
        const circleClass = e.stretch ? "stretch" : "hang"

        items.push(
          new CircleObject({
            left: e.left || 0,
            top: e.top || 0,
            radius: (e.diameter || 0) / 2,
            stroke: e.stretch ? "transparent" : holeStroke,
            strokeWidth: outerWidth,
            fill: e.stretch ? "transparent" : holeFill,
          }).addClass(circleClass, "custom_holes")
        )
      }
    }

    if (customHoleTabOpened && holesOverlayFlag && !isMobile()) {
      this.addCustomHoleDimensions()
    }

    return [
      new GroupObject(items, {
        left: 0,
        top: 0,
        width: widthPx,
        height: heightPx,
      }),
    ]
  }

  private widthDimensionsGroup(x1: number, x2: number, labelValue: number) {
    const { widthPx, heightPx } = this.state
    const { centerX, centerY, scale } = this.drawables

    const margin = 20 / scale
    const signLeft = centerX - widthPx / 2
    const signTop = centerY - heightPx / 2
    const label = sizeUnit === "in" ? Calc.pxToIn(labelValue) : Calc.pxToMm(labelValue)
    const textPadding = 8 / scale
    const lineEnd = 9 / scale
    const midX = (x1 + x2) / 2
    const availableWidth = x2 - x1

    const defaults = {
      selectable: false,
      hoverCursor: "cursor",
      objectCaching: false,
    }

    const text = new fabric.IText(`${label} ${sizeUnit}`, {
      fontSize: 12 / scale,
      fontFamily: "Barlow",
      name: "width",
      fill: "#14213D",
      backgroundColor: "#f4f8fa",
    })

    const textWidth = text.getBoundingRect().width + 2 * textPadding
    const isTooShort = availableWidth < textWidth

    const endLines = [x1, x2].map(
      x =>
        new fabric.Line(
          [signLeft + x, signTop - margin - lineEnd / 2, signLeft + x, signTop - margin + lineEnd / 2 + 0.5 / scale],
          { ...defaults, strokeWidth: 1 / scale, stroke: "#74848D" }
        )
    )

    if (isTooShort) {
      const line = new fabric.Line([signLeft + x1, signTop - margin, signLeft + x2, signTop - margin], {
        ...defaults,
        strokeWidth: 1 / scale,
        stroke: "#74848D",
      })

      text.set({
        left: signLeft + x1 + availableWidth / 2 - text.getBoundingRect().width / 2,
        top: signTop - margin - text.getBoundingRect().height - 4 / scale,
      })

      return new fabric.Group([line, ...endLines, text], {
        name: "width_custom_dimensions",
        ...defaults,
      })
    }

    const leftLine = new fabric.Line(
      [
        signLeft + x1,
        signTop - margin,
        signLeft + (midX - text.getBoundingRect().width / 2) - textPadding,
        signTop - margin,
      ],
      { ...defaults, strokeWidth: 1 / scale, stroke: "#74848D" }
    )

    const rightLine = new fabric.Line(
      [
        signLeft + (midX + text.getBoundingRect().width / 2) + textPadding,
        signTop - margin,
        signLeft + x2,
        signTop - margin,
      ],
      { ...defaults, strokeWidth: 1 / scale, stroke: "#74848D" }
    )

    text.set({
      left: signLeft + midX - text.getBoundingRect().width / 2,
      top: signTop - margin - text.getBoundingRect().height / 2,
    })

    return new fabric.Group([leftLine, rightLine, ...endLines, text], {
      name: "width_custom_dimensions",
      ...defaults,
    })
  }

  private heightDimensionsGroup(y1: number, y2: number, labelValue: number) {
    const { widthPx, heightPx } = this.state
    const { centerX, centerY, scale } = this.drawables

    const margin = 20 / scale
    const signLeft = centerX - widthPx / 2
    const signTop = centerY - heightPx / 2
    const label = sizeUnit === "in" ? Calc.pxToIn(labelValue) : Calc.pxToMm(labelValue)
    const textPadding = 8 / scale
    const lineEnd = 9 / scale
    const midY = (y1 + y2) / 2
    const availableHeight = y2 - y1

    const heightText = new fabric.IText(`${label} ${sizeUnit}`, {
      fontSize: 12 / scale,
      fontFamily: "Barlow",
    })
    const defaults = {
      selectable: false,
      hoverCursor: "cursor",
      objectCaching: false,
    }

    const textHeight = heightText.getBoundingRect().width + 2 * textPadding
    const isTooShort = availableHeight < textHeight

    const endLines = [y1, y2].map(
      y =>
        new fabric.Line(
          [signLeft - margin - lineEnd / 2, signTop + y, signLeft - margin + lineEnd / 2 + 0.5 / scale, signTop + y],
          { ...defaults, strokeWidth: 1 / scale, stroke: "#74848D" }
        )
    )

    const heightGuideConfig = {
      name: "height",
      fontSize: 12 / scale,
      left: signLeft - margin + heightText.getBoundingRect().height / 2,
      top: signTop + midY - heightText.getBoundingRect().width / 2,
      angle: 90,
      fill: "#14213D",
      fontFamily: "Barlow",
      backgroundColor: "#f4f8fa",
      ...defaults,
    }

    if (isTooShort) {
      const line = new fabric.Line([signLeft - margin, signTop + y1, signLeft - margin, signTop + y2], {
        ...defaults,
        strokeWidth: 1 / scale,
        stroke: "#74848D",
      })

      const heightGuideText = new fabric.IText(`${label} ${sizeUnit}`, {
        ...heightGuideConfig,
        left: signLeft - margin + heightText.getBoundingRect().height + 4 / scale,
        top: signTop + y1 + availableHeight / 2 - heightText.getBoundingRect().width / 2,
      })

      return new fabric.Group([line, ...endLines, heightGuideText], { name: "height_custom_dimensions", ...defaults })
    }

    const topLine = new fabric.Line(
      [
        signLeft - margin,
        signTop + y1,
        signLeft - margin,
        signTop + midY + (heightText.getBoundingRect().width / 2 + textPadding),
      ],
      { ...defaults, strokeWidth: 1 / scale, stroke: "#74848D" }
    )

    const bottomLine = new fabric.Line(
      [
        signLeft - margin,
        signTop + midY - heightText.getBoundingRect().width / 2 - textPadding,
        signLeft - margin,
        signTop + y2,
      ],
      { ...defaults, strokeWidth: 1 / scale, stroke: "#74848D" }
    )

    const heightGuideText = new fabric.IText(`${label} ${sizeUnit}`, heightGuideConfig)

    return new fabric.Group([topLine, bottomLine, ...endLines, heightGuideText], {
      name: "height_custom_dimensions",
      ...defaults,
    })
  }

  topDrawable() {
    return this.buildDrawable("#aaa", "#F5F7F6", 1 / this.drawables.scale)
  }

  engravingDrawable() {
    return this.buildDrawable(this.drawables.engravingColor, "transparent", this.drawables.engravingStrokeWidth)
  }
}
